import styled, { css } from 'styled-components';
import { styles } from '../constants/styles.js';
import { spacing } from '../constants/spacing.js';
import { boxStyles } from '../Box/styles.js';
import { px2rem } from '../utils/helpers/style.js';
import { boxPropExclusions } from '../Box/props.js';
import { shadows } from '../constants/shadows.js';

/**
 * Return given full screen or windowed modal styles according to:
 *
 * ```
 * fullScreen | XS | S | M | L | XL
 * -----------+----+---+---+---+----
 * undefined  | W  | W | W | W | W
 * "desktop"  | W  | F | F | F | F
 * "mobile"   | F  | W | W | W | W
 * "both"     | F  | F | F | F | F
 * ```
 *
 */
var getModalStyles = function getModalStyles(fullscreenStyles, windowedStyles, kind) {
  return function (props) {
    if (!props.$fullScreen) return windowedStyles;
    if (props.$fullScreen === 'both') return fullscreenStyles;
    if (kind === 'mobile') {
      return props.$fullScreen === 'mobile' ? fullscreenStyles : windowedStyles;
    }
    if (kind === 'desktop') {
      return props.$fullScreen === 'desktop' ? fullscreenStyles : windowedStyles;
    }
  };
};
var windowedModalStyles = /*#__PURE__*/css(["margin:0 ", ";", "{margin:0 ", ";}", "{margin:0 ", ";}", "{margin:0 ", ";}", "{margin:0 ", ";}max-height:90%;max-width:", ";transform:translateY(", ");"], spacing.xxs, styles.breakpoint.xs, spacing.xs, styles.breakpoint.s, spacing.s, styles.breakpoint.m, spacing.xl, styles.breakpoint.l, spacing.xxl, function (props) {
  return props.style && props.style.maxWidth ? props.style.maxWidth : '55em';
}, function (props) {
  return props.$animateIn ? 0 : spacing.xxs;
});
var fullScreenModalStyles = /*#__PURE__*/css(["max-height:unset;max-width:unset;transform:unset;height:100%;width:100%;"]);
var content = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__content",
  componentId: "sc-me768a-0"
})(["margin:0;flex:1;", " text-align:", ";z-index:500;overflow:auto;padding-bottom:", ";scrollbar-width:thin;scrollbar-color:", " ", ";&::-webkit-scrollbar{background:transparent;width:", ";}&::-webkit-scrollbar-track{background:transparent;box-shadow:none;}&::-webkit-scrollbar-thumb{background-color:", ";border:", " solid ", ";box-shadow:none;border-radius:", ";}"], function (props) {
  return props.$noPadding ? null : getPaddingStyles(props.$variant);
}, function (props) {
  return props.$alignContent ? props.$alignContent : 'center';
}, function (props) {
  return props.$noPadding ? 0 : '2rem';
}, function (p) {
  return p.theme.colors.primary;
}, function (p) {
  return p.theme.inputField.web.background;
}, px2rem(8), function (p) {
  return p.theme.colors.primary;
}, px2rem(2), function (p) {
  return p.theme.colors.background;
}, function (p) {
  return p.theme.borders.radius;
});
var header = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__header",
  componentId: "sc-me768a-1"
})(["display:flex;flex-direction:column;padding:", ";background-color:", ";z-index:501;transition:background-color ", ",box-shadow ", ",border ", ";transition-timing-function:", ";box-shadow:", ";&:empty{display:none;}"], px2rem(16), function (p) {
  return p.$scrolled ? p.theme.colors.background : 'transparent';
}, styles.transition.timing.slower, styles.transition.timing.slower, styles.transition.timing.slower, styles.transition["function"].base, function (p) {
  return p.$scrolled ? shadows.s : undefined;
});
var modal = /*#__PURE__*/styled.div.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !boxPropExclusions.has(prop);
  }
}).withConfig({
  displayName: "styles__modal",
  componentId: "sc-me768a-2"
})(["background:", ";overflow:auto;position:relative;transition:transform ", ";transition-delay:", ";transition-timing-function:", ";display:flex;flex-direction:column;justify-content:center;", "{", "}", "{", "}:focus-visible{outline-offset:0;box-shadow:0 0 0 4px ", ";outline:2px solid ", ";}border-radius:", ";", ":empty ~ ", "{padding-top:", ";}", ""], function (p) {
  return p.theme.colors.background;
}, styles.transition.timing.slower, styles.transition.timing.extraFast, styles.transition["function"].base, styles.breakpointMax.s, getModalStyles(fullScreenModalStyles, windowedModalStyles, 'mobile'), styles.breakpoint.s, getModalStyles(fullScreenModalStyles, windowedModalStyles, 'desktop'), function (p) {
  return p.theme.tokens.focus.primary;
}, function (p) {
  return p.theme.tokens.focus.secondary;
}, function (p) {
  return p.$fullScreen ? 0 : p.theme.borders.radius;
}, header, content, function (p) {
  return p.$noPadding ? 0 : px2rem(72);
}, boxStyles);
var getPaddingStyles = function getPaddingStyles(variant) {
  if (variant === 'condensed') {
    return css(["margin:0 auto;padding:", " ", " ", " ", ";", "{padding:", ";}"], spacing.m, spacing.xxs, spacing.m, spacing.xxs, styles.breakpoint.s, spacing.m);
  } else {
    return css(["margin:0 auto;padding:0.5rem ", " ", " ", ";", "{padding:0.5rem ", " ", " ", ";}", "{padding:0.5rem ", " ", " ", ";}", "{padding:0.5rem ", " ", " ", ";}", "{padding:0.5rem ", " ", " ", ";}", "{padding:0.5rem ", " ", " ", ";}"], spacing.xxs, spacing.xl, spacing.xxs, styles.breakpoint.xs, spacing.xs, spacing.xl, spacing.xs, styles.breakpoint.s, spacing.s, spacing.xl, spacing.s, styles.breakpoint.m, spacing.xl, spacing.xl, spacing.xl, styles.breakpoint.l, spacing.xxl, spacing.xl, spacing.xxl, styles.breakpoint.xxl, spacing.xxxl, spacing.xl, spacing.xxxl);
  }
};

export { content, header, modal };
